import React from "react";
import { motion } from "framer-motion";
import { Italic, Eyebrow } from "./_primitives";
import { LEADERSHIP, TEAM_MEMBERS, ADVISORS } from "../../data/sparkseed";

const initials = (name) =>
  name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

export default function Team() {
  return (
    <section id="team" data-testid="team-section" className="bg-[#F7F8EF] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="max-w-3xl mb-12">
          <Eyebrow>The Team</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-bold text-[#0F2818]">
            Leadership That's <Italic>Built</Italic> Companies.
          </h2>
        </div>

        {/* Watercolor team portrait — visual hero between headline and founder cards */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 lg:mb-16 relative"
          data-testid="team-watercolor"
        >
          <div className="relative rounded-3xl overflow-hidden border border-[#14401F]/10 bg-white">
            <img
              src="/team/team-watercolor.jpg"
              alt="The SparkSeed Ventures team — illustrated portrait"
              className="w-full h-auto block"
              loading="lazy"
            />
            {/* Soft caption ribbon */}
            <div className="absolute left-5 bottom-5 lg:left-7 lg:bottom-7 px-4 py-2 rounded-full bg-white/85 backdrop-blur border border-[#14401F]/10">
              <span className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#14401F]">
                The Whole Bench · 9 Operators
              </span>
            </div>
          </div>
        </motion.div>

        {/* Leadership - 3 in a row */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
          {LEADERSHIP.map((l, i) => (
            <div
              key={l.name}
              data-testid={`leadership-${i}`}
              className="bg-[#14401F] text-white rounded-3xl p-8 lg:p-10 relative overflow-hidden group"
            >
              <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-[#B8D958]/10 group-hover:bg-[#B8D958]/15 transition-colors" />
              <div className="relative z-10">
                <div className="h-20 w-20 rounded-2xl overflow-hidden mb-6 flex items-center justify-center font-serif-display text-3xl font-bold bg-[#B8D958] text-[#0F2818]">
                  {l.img ? (
                    <img src={l.img} alt={l.name} className="h-full w-full object-cover" />
                  ) : (
                    l.initials
                  )}
                </div>
                <h3 className="font-serif-display text-3xl font-bold leading-tight">
                  {l.name}
                </h3>
                <div className="mt-2 text-sm uppercase tracking-[0.2em] text-[#B8D958] font-semibold">
                  {l.role}
                </div>
                <ul className="mt-6 space-y-2.5">
                  {l.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-3 text-white/80">
                      <span className="mt-2 h-1 w-3 bg-[#B8D958] flex-shrink-0" />
                      <span className="text-sm leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Team grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {TEAM_MEMBERS.map((m, i) => (
            <motion.div
              key={m.name}
              data-testid={`team-member-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: (i % 3) * 0.08 + Math.floor(i / 3) * 0.12, duration: 0.45 }}
              className="bg-white rounded-2xl p-5 border border-[#14401F]/10 flex items-start gap-4 ss-tilt ss-shine"
            >
              <div
                className="h-14 w-14 rounded-xl overflow-hidden flex items-center justify-center font-serif-display font-bold text-lg flex-shrink-0"
                style={{
                  background: i % 2 === 0 ? "#DCE8C9" : "#14401F",
                  color: i % 2 === 0 ? "#14401F" : "#B8D958",
                }}
              >
                {m.img ? (
                  <img src={m.img} alt={m.name} className="h-full w-full object-cover" />
                ) : (
                  initials(m.name)
                )}
              </div>
              <div className="min-w-0">
                <h4 className="font-serif-display text-base font-bold text-[#0F2818] leading-tight">
                  {m.name}
                </h4>
                <div className="text-xs uppercase tracking-[0.15em] text-[#14401F] font-semibold mt-1">
                  {m.role}
                </div>
                <p className="mt-1.5 text-xs text-[#4A5A4F] leading-snug">{m.bg}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer count */}
        <div className="mt-10 flex flex-wrap gap-3">
          {[
            "2 Associate Directors",
            "3 Investment Managers",
            "2 Investment Analysts",
            "50+ Operator Mentors",
          ].map((c) => (
            <span
              key={c}
              className="px-4 py-2 rounded-full border border-[#14401F]/20 text-sm text-[#0F2818]"
            >
              {c}
            </span>
          ))}
        </div>

        {/* Advisors */}
        <div className="mt-24 lg:mt-28" data-testid="advisors-block">
          <div className="max-w-3xl mb-10">
            <Eyebrow color="#C7913A">Advisors · Board of Elders</Eyebrow>
            <h3 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight font-bold text-[#0F2818]">
              Counsel from those who have <Italic className="text-[#C7913A]">already built</Italic> at scale.
            </h3>
            <p className="mt-4 text-sm text-[#4A5A4F] max-w-xl">
              Industrial and manufacturing veterans who&apos;ve navigated
              decades of building enduring Indian businesses — now guiding
              SparkSeed&apos;s founders and fund architecture.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {ADVISORS.map((a, i) => (
              <motion.div
                key={a.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`advisor-${i}`}
                className="group relative bg-white rounded-3xl p-6 lg:p-7 border border-[#14401F]/10 overflow-hidden flex gap-5 items-start hover:shadow-[0_18px_40px_-20px_rgba(20,64,31,0.22)] transition-all"
              >
                {/* Amber corner glow */}
                <div className="absolute -right-16 -top-16 w-44 h-44 rounded-full bg-[#C7913A]/8 group-hover:bg-[#C7913A]/15 transition-colors pointer-events-none" />
                <div className="relative z-10 shrink-0 h-24 w-24 lg:h-28 lg:w-28 rounded-2xl overflow-hidden bg-[#F7F8EF] flex items-center justify-center">
                  {a.img ? (
                    <img src={a.img} alt={a.name} className="h-full w-full object-cover" />
                  ) : (
                    <span className="font-serif-display text-3xl font-bold text-[#14401F]">{initials(a.name)}</span>
                  )}
                </div>
                <div className="relative z-10 flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] font-bold text-[#C7913A]">Advisor · 0{i + 1}</span>
                  </div>
                  <h4 className="font-serif-display text-xl lg:text-2xl font-bold text-[#0F2818] leading-tight">
                    {a.name}
                  </h4>
                  <div className="mt-1 text-sm font-semibold text-[#14401F]">
                    {a.role}
                  </div>
                  <p className="mt-3 text-sm text-[#4A5A4F] leading-relaxed">
                    {a.bg}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
